import { inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { select } from '@ngneat/elf';
import { StoreService } from '~ngx-shared/services';
import {
  DataProviderOptionModel,
  GraphQlColumnModel,
  GraphQlFilterModel,
  GraphQlSortModel
} from '../models';

export type GraphQlKeyState = {
  option: DataProviderOptionModel;
  columns: GraphQlColumnModel[];
  meta?: Data;
};

type GraphQlStore = {
  data: {
    [key: string]: GraphQlKeyState;
  };
};

@Injectable({
  providedIn: 'root'
})
export class GraphQlService {
  private readonly storeService = inject(StoreService);

  graphQlStore = this.storeService.createStore<GraphQlStore>({
    name: 'graphql',
    initial: { data: {} },
    persist: { storage: 'local' }
  });

  private prefixAction: () => string;

  init(prefixAction: () => string) {
    this.prefixAction = prefixAction;
  }
  private getPrefixedKey(key: string | undefined) {
    if (key && this.prefixAction != null) {
      return `${this.prefixAction()}${key}`;
    }
    return key;
  }

  state$(key: string) {
    key = this.getPrefixedKey(key) as string;
    return this.graphQlStore.pipe(select(state => state.data?.[key]));
  }

  sortBy$(key: string) {
    key = this.getPrefixedKey(key) as string;
    return this.graphQlStore.pipe(select(state => state.data?.[key]?.option?.sortBy));
  }

  filter$(key: string) {
    key = this.getPrefixedKey(key) as string;
    return this.graphQlStore.pipe(select(state => state.data?.[key]?.option?.filter));
  }

  reset() {
    this.graphQlStore.update(() => ({ data: {} }));
  }

  updateSort(key: string | undefined, sortBy?: GraphQlSortModel[]) {
    key = this.getPrefixedKey(key);
    if (key) {
      this.graphQlStore.update(state => {
        return {
          ...state,
          data: {
            ...state.data,
            [key]: {
              ...state.data?.[key],
              option: {
                ...state.data?.[key]?.option,
                offset: 0,
                sortBy
              }
            }
          }
        };
      });
    }
  }

  updateFilter(key: string | undefined, filter?: GraphQlFilterModel, meta?: Data) {
    key = this.getPrefixedKey(key);
    if (key) {
      this.graphQlStore.update(state => {
        return {
          ...state,
          data: {
            ...state.data,
            [key]: {
              ...state.data?.[key],
              option: {
                ...state.data?.[key]?.option,
                offset: 0,
                filter
              },
              meta
            }
          }
        };
      });
    }
  }

  updateLimitAndOffset(key: string | undefined, limit: number, offset: number) {
    key = this.getPrefixedKey(key);
    if (key) {
      this.graphQlStore.update(state => {
        return {
          ...state,
          data: {
            ...state.data,
            [key]: {
              ...state.data?.[key],
              option: {
                ...state.data?.[key]?.option,
                limit,
                offset
              }
            }
          }
        };
      });
    }
  }

  updateColumns(key: string | undefined, columns: GraphQlColumnModel[]) {
    key = this.getPrefixedKey(key);
    if (key) {
      this.graphQlStore.update(state => {
        return {
          ...state,
          data: {
            ...state.data,
            [key]: {
              ...state.data?.[key],
              columns: columns
                .filter(column => !column.isNotSelectable)
                .map(column => {
                  return {
                    label: column.label,
                    path: column.path,
                    hidden: column.hidden
                  };
                })
            }
          }
        };
      });
    }
  }

  clearColumns(key: string | undefined) {
    key = this.getPrefixedKey(key);
    if (key) {
      this.graphQlStore.update(state => {
        return {
          ...state,
          data: {
            ...state.data,
            [key]: {
              ...state.data?.[key],
              columns: [],
              option: {
                ...state.data?.[key].option,
                limit: 10,
                offset: 0
              }
            }
          }
        };
      });
    }
  }
}
